import { useCallback, useEffect, useRef, useState } from "react"
import type { ComponentProps } from "react"
import { Modal } from "components/ui/modals/Modal/Modal"
import style from "./SetPageNameModal.module.scss"

type Props = {
	isOpen: boolean
	onClose: () => void
	/** Called with the trimmed page name when the user submits. */
	onConfirm: (name: string) => void
	/** Current page name, prefilled into the input (null when the page is unnamed). */
	currentName?: string | null
	isPending?: boolean
	/** Backend validation / conflict message to surface under the input (e.g. unknown page name). */
	errorMessage?: string | null
	// When provided, overrides the default WebViewer shadow-root portal logic.
	getContainer?: ComponentProps<typeof Modal>["getContainer"]
}

/**
 * "Set page name" modal (Figma) — sets/edits a page's name, which the backend applies to every
 * elevation on that page. Reuses the shadow-root portal pattern of `ConnectToElevationModal` /
 * `ConfirmModal` so it renders inside the Apryse shadow DOM. Free-text input per the design; the
 * backend validates the name (REQ-275 list) and any rejection is shown inline via `errorMessage`.
 */
export const SetPageNameModal = ({
	isOpen,
	onClose,
	onConfirm,
	currentName,
	isPending = false,
	errorMessage,
	getContainer,
}: Props) => {
	const anchorRef = useRef<HTMLDivElement | null>(null)
	const portalRef = useRef<HTMLDivElement | null>(null)
	const [name, setName] = useState("")
	// Whether the user has edited the field since the last backend rejection. While true we suppress
	// the error styling/message so typing immediately clears the stale "invalid" state (the field is
	// free-text — there's no client-side restriction; letters must never look rejected as you type).
	const [errorDismissed, setErrorDismissed] = useState(false)

	useEffect(() => {
		return () => {
			portalRef.current?.remove()
			portalRef.current = null
		}
	}, [])

	// Seed with the current name (empty for an unnamed page) whenever the modal (re)opens, so a
	// prior edit doesn't leak in and the user starts from the existing value.
	useEffect(() => {
		if (isOpen) setName(currentName ?? "")
	}, [isOpen, currentName])

	// A fresh backend error (or reopening the modal) re-arms the error display.
	useEffect(() => {
		setErrorDismissed(false)
	}, [errorMessage, isOpen])

	const defaultGetContainer = useCallback((): HTMLElement => {
		if (portalRef.current?.isConnected) return portalRef.current
		const root = anchorRef.current?.getRootNode()
		const host: ShadowRoot | HTMLElement =
			root instanceof ShadowRoot
				? root
				: ((document.querySelector("apryse-webviewer") as HTMLElement | null)?.shadowRoot ?? document.body)
		const container = document.createElement("div")
		container.setAttribute("data-portal", "set-page-name-modal")
		host.appendChild(container)
		portalRef.current = container
		return container
	}, [])

	const resolvedGetContainer = getContainer !== undefined ? getContainer : defaultGetContainer

	const trimmed = name.trim()
	const canSubmit = trimmed.length > 0 && !isPending
	// Only surface the backend error while the user hasn't started correcting it.
	const visibleError = errorDismissed ? null : errorMessage

	const handleConfirm = () => {
		if (!canSubmit) return
		onConfirm(trimmed)
	}

	return (
		<>
			<div ref={anchorRef} style={{ display: "none" }} aria-hidden />
			<Modal isModalOpen={isOpen} setModalClose={onClose} width={456} getContainer={resolvedGetContainer}>
				<div className={style.body} data-testid="set-page-name-modal">
					<div className={style.header}>
						<h3 className={style.title}>Set page name</h3>
						<p className={style.description}>This name will be applied to all elevations on this page</p>
					</div>
					<div className={style.field}>
						<label className={style.fieldLabel} htmlFor="set-page-name-input">
							Page name
						</label>
						<input
							id="set-page-name-input"
							type="text"
							className={`${style.input}${visibleError ? ` ${style.inputError}` : ""}`}
							placeholder="e.g. I 301"
							value={name}
							disabled={isPending}
							autoComplete="off"
							// Focus the sole input when this single-purpose dialog opens so the user can type immediately.
							autoFocus
							onChange={(e) => {
								setName(e.target.value)
								setErrorDismissed(true)
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault()
									handleConfirm()
								}
							}}
						/>
						{visibleError ? (
							<span className={style.errorMessage} role="alert">
								{visibleError}
							</span>
						) : null}
					</div>
					<div className={style.actions}>
						<button type="button" className={style.cancelButton} onClick={onClose} disabled={isPending}>
							Cancel
						</button>
						<button
							type="button"
							className={style.confirmButton}
							onClick={handleConfirm}
							disabled={!canSubmit}
						>
							Save
						</button>
					</div>
				</div>
			</Modal>
		</>
	)
}
